const MIN_TRUNCATED_TAG_WIDTH = 36

export type FittedLine = {
  start: number
  count: number
  truncateWidth: number | null
}

// Fill each line with whole tags. A tag that does not fit moves to the next
// line. Only the last line ellipsizes, unless a tag is wider than every line.
export function fitTagWidths({
  widths,
  available,
  gap,
  chipWidth,
  separatorWidth = 0,
  maxDisplay,
  maxLines = 1,
  minTruncate = MIN_TRUNCATED_TAG_WIDTH,
}: {
  widths: number[]
  available: number
  gap: number
  chipWidth: number
  separatorWidth?: number
  maxDisplay: number
  maxLines?: number
  minTruncate?: number
}): FittedLine[] {
  if (available <= 0 || widths.length === 0 || maxDisplay <= 0 || maxLines <= 0) {
    return []
  }

  const between = gap + (separatorWidth > 0 ? separatorWidth + gap : 0)
  const lines: FittedLine[] = []
  let lineIndex = 0
  let lineStart = 0
  let lineCount = 0
  let used = 0
  let index = 0

  const closeLine = (truncateWidth: number | null) => {
    if (lineCount === 0) return
    lines.push({ start: lineStart, count: lineCount, truncateWidth })
    lineCount = 0
  }

  while (index < widths.length && index < maxDisplay && lineIndex < maxLines) {
    const isLastLine = lineIndex === maxLines - 1
    const width = widths[index]
    const gapBefore = lineCount > 0 ? between : 0
    const hiddenAfter = widths.length - (index + 1)
    const chip = isLastLine && hiddenAfter > 0 ? gap + chipWidth : 0

    if (used + gapBefore + width + chip <= available + 0.5) {
      used += gapBefore + width
      lineCount += 1
      index += 1
      continue
    }

    if (!isLastLine) {
      if (lineCount === 0 && available >= minTruncate) {
        lineCount = 1
        index += 1
        closeLine(Math.floor(available))
        lineIndex += 1
        lineStart = index
        used = 0
        continue
      }
      closeLine(null)
      lineIndex += 1
      lineStart = index
      used = 0
      continue
    }

    const room = available - used - gapBefore - chip
    if (room >= minTruncate) {
      lineCount += 1
      closeLine(Math.floor(room))
    } else {
      closeLine(null)
    }
    break
  }

  closeLine(null)
  return lines
}

function horizontalMargin(style: CSSStyleDeclaration) {
  return (Number.parseFloat(style.marginLeft) || 0) + (Number.parseFloat(style.marginRight) || 0)
}

function isOutOfFlow(style: CSSStyleDeclaration) {
  return style.display === 'none' || style.position === 'absolute' || style.position === 'fixed'
}

// A flex item's width follows its content. Ellipsizing the tag makes the next
// measurement smaller, so use the free space in the flex row, after siblings.
export function tagListAvailableWidth(container: HTMLElement) {
  const own = container.clientWidth
  const parent = container.parentElement
  if (!parent) return own

  const parentStyle = getComputedStyle(parent)
  const parentIsFlex = parentStyle.display === 'flex' || parentStyle.display === 'inline-flex'
  if (!parentIsFlex) return own

  const parentContent =
    parent.clientWidth -
    (Number.parseFloat(parentStyle.paddingLeft) || 0) -
    (Number.parseFloat(parentStyle.paddingRight) || 0)

  const direction = parentStyle.flexDirection
  const isColumn = direction === 'column' || direction === 'column-reverse'
  if (isColumn) return Math.max(own, parentContent)

  let siblings = 0
  let childCount = 0
  for (const child of parent.children) {
    if (!(child instanceof HTMLElement)) continue
    const childStyle = getComputedStyle(child)
    if (isOutOfFlow(childStyle)) continue
    childCount += 1
    if (child === container) continue
    siblings += child.getBoundingClientRect().width + horizontalMargin(childStyle)
  }

  const gap = Number.parseFloat(parentStyle.columnGap) || 0
  const gaps = Math.max(0, childCount - 1) * gap
  const room = parentContent - siblings - gaps - horizontalMargin(getComputedStyle(container))
  return Math.max(own, room)
}
